import { SnackbarManager } from '@cmg/design-system';
import isNil from 'lodash/isNil';
import React from 'react';

import {
  CompanySearch_Issuer2PartsFragment,
  useCompanySearch_IssuerListLazyQuery,
} from '../graphql/__generated__/CompanySearch_IssuerList';
import type { CompanyOption } from '../types';

export type SearchIssuerFilterHookResult = {
  searchOptions: (searchTerm: string) => Promise<CompanyOption[]>;
};

/**
 * Issuer search functionality for CompanySearch widget
 */
export const useIssuerSearch = (): SearchIssuerFilterHookResult => {
  const [searchIssuers] = useCompanySearch_IssuerListLazyQuery();

  const searchOptions = React.useCallback(
    async (newSearchTerm: string) => {
      // see the need for uppercase in the hook's JSDoc
      const searchTerm = newSearchTerm.trim();

      const issuers: CompanySearch_Issuer2PartsFragment[] = await searchIssuers({
        variables: {
          searchTerm: searchTerm,
        },
      })
        .then(({ data }) => data?.searchIssuers ?? [])
        .catch(() => {
          SnackbarManager.error('Error occurred while loading issuers');
          return [];
        });

      return issuers.reduce<CompanyOption[]>(
        (companyOptions, { id, name, primarySymbol, snippet }) => {
          if (!isNil(id)) {
            companyOptions.push({
              value: id,
              label: name,
              ticker: primarySymbol!,
              snippet: snippet,
            });
          }
          return companyOptions;
        },
        []
      );
    },
    [searchIssuers]
  );

  return { searchOptions };
};
